<script setup lang="ts">
import CloseIcon from '../../../assets/icons/close.svg?component'
import { BoxComponent, FlexComponent, TypographyComponent } from '../../index'

const props = defineProps<{
  isDeletable: boolean
  campaignId: number
  campaignLink: string | undefined
  isInternalLink: boolean
}>()

const emits = defineEmits<(event: 'delete', campaignId: number) => void>()

const handleDelete = () => {
  emits('delete', props.campaignId)
}
</script>

<template>
  <FlexComponent class="deletable-campaign-item" align="center" justify="end" :gap="4">
    <button
      v-if="props.isDeletable"
      class="deletable-campaign-item__delete"
      type="button"
      aria-label="Remove campaign"
      @click="handleDelete"
    >
      <span class="deletable-campaign-item__delete-icon">
        <CloseIcon />
      </span>
    </button>
    <BoxComponent :max-w="200" overflow-hidden>
      <router-link v-if="campaignLink && isInternalLink" :to="campaignLink!">
        <TypographyComponent variant="body-medium" text-align="right" text-decoration="underline" text-truncate cursor="pointer">
          <slot></slot>
        </TypographyComponent>
      </router-link>
      <a v-else-if="campaignLink" :href="campaignLink">
        <TypographyComponent variant="body-medium" text-align="right" text-decoration="underline" text-truncate cursor="pointer">
          <slot></slot>
        </TypographyComponent>
      </a>
      <TypographyComponent v-else variant="body-medium" text-align="right" text-truncate>
        <slot></slot>
      </TypographyComponent>
    </BoxComponent>
  </FlexComponent>
</template>

<style scoped lang="scss">
.deletable-campaign-item__delete {
  // hidden with opacity rather than `visibility` or `width: 0`: it keeps its space, so revealing
  // it never shifts the campaign name, and it stays focusable, which is what :focus-within needs
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  opacity: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
    border-radius: 4px;
  }

  :deep(path) {
    transition: fill 0.5s ease;
  }

  :deep(svg) {
    display: block;
  }
}

// the parent detail row marks itself `.content-item-campaign-row` to become the hover zone:
// the campaign name on its own is far too small a target to find the delete button by
.content-item-campaign-row:hover .deletable-campaign-item__delete,
.deletable-campaign-item:hover .deletable-campaign-item__delete,
.deletable-campaign-item:focus-within .deletable-campaign-item__delete {
  opacity: 1;
}

.deletable-campaign-item__delete:is(:hover, :focus-visible) :deep(path) {
  fill: var(--danger);
}

.deletable-campaign-item__delete-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  transition: background-color var(--transition) ease;
}

.deletable-campaign-item__delete:is(:hover, :focus-visible) .deletable-campaign-item__delete-icon {
  background-color: var(--button-hover);
}
</style>
